import styled from '@emotion/styled';
import { Stack, Text, tokens } from '@hotjar/hotsauce';
import type { EnhancedPlan } from '@hotjar/product-catalog';
import { useTranslation } from '@hotjar/translations';
import type { Currency } from '~/subscription-management/api/common/model/customer';
import { usePrettifyPrice } from '~/subscription-management/utils/cost/prettify-price';

const S = {
  PrettifiedPrice: styled(Text)`
    ${tokens.text('header5')};
    line-height: 20px;
    color: inherit;
  `,
};

interface Props {
  plan: EnhancedPlan['name'];
  currency: Currency;
  price: number;
  billingPeriod: 'MONTHLY' | 'ANNUALLY';
}

export function Pricing({ plan, currency, price, billingPeriod }: Props): JSX.Element {
  const { t: tSubscriptions } = useTranslation('checkout', { keyPrefix: 'wizard.subscriptions' });
  const { t: tCommon } = useTranslation('checkout', { keyPrefix: 'wizard.common' });

  const { prettifyPrice } = usePrettifyPrice(currency, 0);
  const [prettifiedCurrency, ...prettifiedPrice] = prettifyPrice(price, 'array');

  const getBillingPeriodPostfix = (): string => {
    if (plan === 'basic') {
      return `/${tSubscriptions('forever')}`;
    }

    if (billingPeriod === 'ANNUALLY') {
      return tCommon('billing-period-annually');
    }

    return tCommon('billing-period-monthly');
  };

  return (
    <Stack spacing="x0pt5">
      <Text textType="subtitle2" textColor="inherited">
        {prettifiedCurrency}
      </Text>
      {/* e.g. ['1', '.', '2', '3'] → '1.23' */}
      <S.PrettifiedPrice>{prettifiedPrice.join('')}</S.PrettifiedPrice>
      <Text textType="subtitle2" textColor="inherited">
        {getBillingPeriodPostfix()}
      </Text>
    </Stack>
  );
}
